import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Icon from "../components/ui/Icon";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";

const PAGINA = 15;

function hojeStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function primeiroDiaDoMes() {
  return `${hojeStr().slice(0, 7)}-01`;
}
function formatarData(iso) {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

const TIPO_JUSTIFICATIVA = [
  { value: "atestado", label: "Atestado médico" },
  { value: "ferias", label: "Férias" },
  { value: "outro", label: "Outro motivo" },
];

function selectStyle() {
  return { width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text };
}
function labelStyle() {
  return { fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 };
}

export default function AtrasosFaltas() {
  const { token } = useAuth();
  const [incidentes, setIncidentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataInicio, setDataInicio] = useState(primeiroDiaDoMes());
  const [dataFim, setDataFim] = useState(hojeStr());
  const [visivel, setVisivel] = useState(PAGINA);
  const [funcionarioAberto, setFuncionarioAberto] = useState(null); // null = lista agregada

  // Filtros da tabela agregada (por funcionário), um por coluna, embutidos
  // no cabeçalho — client-side, sobre os incidentes já buscados no período.
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroSetor, setFiltroSetor] = useState("");
  const [filtroFaltas, setFiltroFaltas] = useState("");
  const [filtroAtrasos, setFiltroAtrasos] = useState("");

  // Filtros da tabela de detalhe (incidentes de UM funcionário), resetados
  // sempre que se abre um funcionário diferente.
  const [filtroTipoDetalhe, setFiltroTipoDetalhe] = useState("");
  const [filtroDataDetalhe, setFiltroDataDetalhe] = useState("");

  const [modalAberto, setModalAberto] = useState(false);
  const [incidenteAtual, setIncidenteAtual] = useState(null);
  const [formJustificativa, setFormJustificativa] = useState({ tipo: "atestado", observacao: "" });
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ data_inicio: dataInicio, data_fim: dataFim });
      const data = await apiFetch("GET", `/api/relatorios/atrasos-faltas?${query}`, undefined, token);
      setIncidentes(data);
    } finally {
      setLoading(false);
    }
  }, [token, dataInicio, dataFim]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { setVisivel(PAGINA); }, [dataInicio, dataFim, filtroNome, filtroEmpresa, filtroSetor, filtroFaltas, filtroAtrasos]);
  useEffect(() => { setFiltroTipoDetalhe(""); setFiltroDataDetalhe(""); }, [funcionarioAberto]);

  // Agrupa os incidentes (um por dia/tipo) por funcionário — faltas e atrasos
  // dentro do período filtrado, mais a data da ocorrência mais recente
  // (de qualquer um dos dois tipos), pra ordenar por urgência.
  const porFuncionario = useMemo(() => {
    const mapa = new Map();
    for (const inc of incidentes) {
      if (!mapa.has(inc.funcionario_id)) {
        mapa.set(inc.funcionario_id, {
          funcionario_id: inc.funcionario_id, nome: inc.nome, empresa_nome: inc.empresa_nome, setor: inc.setor,
          faltas: 0, atrasos: 0, ultimaData: inc.data,
        });
      }
      const item = mapa.get(inc.funcionario_id);
      if (inc.tipo === "falta") item.faltas += 1;
      else item.atrasos += 1;
      if (inc.data > item.ultimaData) item.ultimaData = inc.data;
    }
    return [...mapa.values()].sort((a, b) => b.ultimaData.localeCompare(a.ultimaData));
  }, [incidentes]);

  const empresasDisponiveis = useMemo(() => [...new Set(porFuncionario.map((f) => f.empresa_nome).filter(Boolean))].sort(), [porFuncionario]);
  const setoresDisponiveis = useMemo(() => [...new Set(porFuncionario.map((f) => f.setor).filter(Boolean))].sort(), [porFuncionario]);

  const porFuncionarioFiltrado = porFuncionario.filter((f) => {
    if (filtroNome && !f.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroEmpresa && f.empresa_nome !== filtroEmpresa) return false;
    if (filtroSetor && f.setor !== filtroSetor) return false;
    if (filtroFaltas === "com" && !(f.faltas > 0)) return false;
    if (filtroFaltas === "sem" && f.faltas > 0) return false;
    if (filtroAtrasos === "com" && !(f.atrasos > 0)) return false;
    if (filtroAtrasos === "sem" && f.atrasos > 0) return false;
    return true;
  });
  const filtrosAgregadoAtivos = Boolean(filtroNome || filtroEmpresa || filtroSetor || filtroFaltas || filtroAtrasos);
  const limparFiltrosAgregado = () => {
    setFiltroNome(""); setFiltroEmpresa(""); setFiltroSetor(""); setFiltroFaltas(""); setFiltroAtrasos("");
  };

  const incidentesDoFuncionario = (funcionarioAberto
    ? incidentes.filter((i) => i.funcionario_id === funcionarioAberto.funcionario_id).sort((a, b) => b.data.localeCompare(a.data))
    : []
  ).filter((i) => {
    if (filtroTipoDetalhe && i.tipo !== filtroTipoDetalhe) return false;
    if (filtroDataDetalhe && i.data !== filtroDataDetalhe) return false;
    return true;
  });
  const filtrosDetalheAtivos = Boolean(filtroTipoDetalhe || filtroDataDetalhe);

  const abrirJustificar = (incidente) => {
    setIncidenteAtual(incidente);
    setFormJustificativa({ tipo: "atestado", observacao: "" });
    setArquivo(null);
    setErro("");
    setModalAberto(true);
  };

  const salvarJustificativa = async () => {
    setErro("");
    setSalvando(true);
    try {
      const form = new FormData();
      // Ordem importa: funcionario_id precisa vir ANTES do arquivo no multipart
      // pra o backend já saber em qual pasta salvar o anexo (ver afastamentos.js).
      form.append("funcionario_id", incidenteAtual.funcionario_id);
      form.append("data_inicio", incidenteAtual.data);
      form.append("data_fim", incidenteAtual.data);
      form.append("tipo", formJustificativa.tipo);
      form.append("observacao", formJustificativa.observacao);
      if (arquivo) form.append("anexo", arquivo);
      await apiFetch("POST", "/api/afastamentos", form, token);
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Atrasos e Faltas
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          {funcionarioAberto
            ? `${funcionarioAberto.nome} — clique em um registro pra justificar (atestado, férias ou outro motivo).`
            : "Funcionários com falta ou atraso no período — clique em um funcionário pra ver o detalhe e justificar."}
        </p>
      </div>

      <div className="no-print" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
        <div style={{ width: 160 }}>
          <Input label="De" type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </div>
        <div style={{ width: 160 }}>
          <Input label="Até" type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </div>
      </div>

      <BarraResumoFiltro
        total={funcionarioAberto ? incidentesDoFuncionario.length : porFuncionarioFiltrado.length}
        totalGeral={funcionarioAberto ? undefined : porFuncionario.length}
        unidade={funcionarioAberto ? "registro(s)" : "funcionário(s)"}
        filtrosAtivos={funcionarioAberto ? filtrosDetalheAtivos : filtrosAgregadoAtivos}
        onLimpar={funcionarioAberto ? () => { setFiltroTipoDetalhe(""); setFiltroDataDetalhe(""); } : limparFiltrosAgregado}
      />

      <Card style={{ padding: 0, marginBottom: 20 }}>
        {loading ? (
          <EstadoCarregando padding={24} />
        ) : funcionarioAberto ? (
          <>
            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${COLORS.border}` }}>
              <button
                onClick={() => setFuncionarioAberto(null)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color: COLORS.primary, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0 }}
              >
                <Icon name="arrowLeft" size={14} /> Voltar pra lista de funcionários
              </button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1.5px solid ${COLORS.border}` }}>
                    <ThFiltroTexto label="Data" type="date" value={filtroDataDetalhe} onChange={setFiltroDataDetalhe} minWidth={140} />
                    <ThFiltroSelect
                      label="Tipo" value={filtroTipoDetalhe} onChange={setFiltroTipoDetalhe} minWidth={130}
                      opcoes={[{ value: "", label: "Todos" }, { value: "atraso", label: "Atraso" }, { value: "falta", label: "Falta" }]}
                    />
                    <ThSimples label="Detalhe" />
                    <ThSimples label="Ação" alinhamento="right" />
                  </tr>
                </thead>
                <tbody>
                  {incidentesDoFuncionario.map((inc, i) => (
                    <tr key={`${inc.data}-${inc.tipo}-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>{formatarData(inc.data)}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <Badge tone="danger">{inc.tipo === "falta" ? "Falta" : "Atraso"}</Badge>
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>
                        {inc.tipo === "atraso" ? `${inc.minutos_atraso} min` : "Dia inteiro"}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <button
                          onClick={() => abrirJustificar(inc)}
                          style={{
                            background: COLORS.primaryBg, color: COLORS.primary, border: "none", borderRadius: 8,
                            padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                          }}
                        >
                          Justificar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : porFuncionario.length === 0 ? (
          <EstadoVazio icon="checkCircle" texto="Nenhum atraso ou falta nesse período." />
        ) : porFuncionarioFiltrado.length === 0 ? (
          <EstadoVazio icon="checkCircle" texto="Nenhum funcionário encontrado com esse filtro." />
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1.5px solid ${COLORS.border}` }}>
                    <ThFiltroTexto label="Nome" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar nome..." minWidth={160} />
                    <ThFiltroSelect
                      label="Empresa" value={filtroEmpresa} onChange={setFiltroEmpresa} minWidth={150}
                      opcoes={[{ value: "", label: "Todas" }, ...empresasDisponiveis.map((e) => ({ value: e, label: e }))]}
                    />
                    <ThFiltroSelect
                      label="Setor" value={filtroSetor} onChange={setFiltroSetor} minWidth={140}
                      opcoes={[{ value: "", label: "Todos" }, ...setoresDisponiveis.map((s) => ({ value: s, label: s }))]}
                    />
                    <ThFiltroSelect
                      label="Faltas" value={filtroFaltas} onChange={setFiltroFaltas} minWidth={120}
                      opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com falta" }, { value: "sem", label: "Sem falta" }]}
                    />
                    <ThFiltroSelect
                      label="Atrasos" value={filtroAtrasos} onChange={setFiltroAtrasos} minWidth={120}
                      opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com atraso" }, { value: "sem", label: "Sem atraso" }]}
                    />
                    <ThSimples label="Última ocorrência" />
                    <ThSimples label="Ação" alinhamento="right" />
                  </tr>
                </thead>
                <tbody>
                  {porFuncionarioFiltrado.slice(0, visivel).map((f) => (
                    <tr
                      key={f.funcionario_id}
                      onClick={() => setFuncionarioAberto(f)}
                      style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer" }}
                    >
                      <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{f.nome}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{f.empresa_nome || "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{f.setor || "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: f.faltas > 0 ? 700 : 400, color: f.faltas > 0 ? COLORS.danger : COLORS.textMid }}>{f.faltas}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: f.atrasos > 0 ? 700 : 400, color: f.atrasos > 0 ? COLORS.danger : COLORS.textMid }}>{f.atrasos}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>{formatarData(f.ultimaData)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setFuncionarioAberto(f); }}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6, background: COLORS.primaryBg, color: COLORS.primary,
                            border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                          }}
                        >
                          Justificar <Icon name="arrowRight" size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visivel < porFuncionarioFiltrado.length && (
              <div style={{ padding: 16, textAlign: "center" }}>
                <Btn variant="secondary" onClick={() => setVisivel((v) => v + PAGINA)}>
                  Ver mais ({porFuncionarioFiltrado.length - visivel} restante{porFuncionarioFiltrado.length - visivel === 1 ? "" : "s"})
                </Btn>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        open={modalAberto}
        onClose={() => setModalAberto(false)}
        title={incidenteAtual ? `Justificar ${incidenteAtual.tipo === "falta" ? "falta" : "atraso"} — ${incidenteAtual.nome}` : ""}
        width={460}
      >
        {incidenteAtual && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ fontSize: 13, color: COLORS.textMid, margin: 0 }}>
              {formatarData(incidenteAtual.data)}
              {incidenteAtual.tipo === "atraso" && ` — ${incidenteAtual.minutos_atraso} min de atraso`}
            </p>

            <div>
              <label style={labelStyle()}>Justificativa</label>
              <select
                value={formJustificativa.tipo}
                onChange={(e) => setFormJustificativa({ ...formJustificativa, tipo: e.target.value })}
                style={selectStyle()}
              >
                {TIPO_JUSTIFICATIVA.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle()}>Observação</label>
              <textarea
                value={formJustificativa.observacao}
                onChange={(e) => setFormJustificativa({ ...formJustificativa, observacao: e.target.value })}
                placeholder="Detalhes da justificativa..."
                rows={3}
                style={{ ...selectStyle(), resize: "vertical", fontFamily: "inherit" }}
              />
            </div>

            <div>
              <label style={labelStyle()}>Anexo (PDF ou foto, opcional)</label>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setArquivo(e.target.files[0] || null)}
                style={{ fontSize: 13 }}
              />
            </div>

            {erro && (
              <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
                {erro}
              </div>
            )}

            <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: 0 }}>
              Não quer registrar nada e deixar como falta sem justificativa? É só fechar essa janela.
            </p>

            <Btn onClick={salvarJustificativa} disabled={salvando}>
              {salvando ? "Salvando..." : "Registrar justificativa"}
            </Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
